import { motion } from "framer-motion";
import { Apple, Star, Phone, MessageSquare, MapPin, Navigation, Check, Clock } from "lucide-react";

const APP_STORE_URL = "https://apps.apple.com/gb/app/clocktower-cars/id591086259";
const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.cordic.epsom.epsomewell";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

/* -------------------- Google-Maps-style SVG canvas -------------------- */
const ROUTE_D = "M64,600 C64,520 128,500 150,430 S232,330 216,250 C210,196 150,168 176,96 C188,64 214,58 250,64";

const GMap = ({ tracking = false, className = "" }) => (
  <svg viewBox="0 0 320 640" preserveAspectRatio="xMidYMid slice" className={`h-full w-full ${className}`} aria-hidden="true">
    <rect width="320" height="640" fill="#e8ecef" />
    {/* parks */}
    <path d="M-10,60 C60,40 110,90 90,150 C70,210 -10,200 -10,200 Z" fill="#cdeacb" />
    <circle cx="270" cy="470" r="80" fill="#cdeacb" opacity="0.9" />
    {/* water */}
    <path d="M0,540 C70,520 90,590 60,640 L0,640 Z" fill="#a9d4ff" />
    <path d="M250,0 C230,60 300,90 320,70 L320,0 Z" fill="#a9d4ff" />
    {/* minor roads */}
    <g stroke="#ffffff" strokeLinecap="round" fill="none">
      <path d="M-10,300 C90,290 210,330 330,300" strokeWidth="9" />
      <path d="M40,-10 C60,160 20,320 70,650" strokeWidth="9" />
      <path d="M-10,470 C120,450 220,500 330,470" strokeWidth="8" />
      <path d="M210,-10 C220,120 180,260 250,650" strokeWidth="8" />
    </g>
    {/* highway */}
    <path d="M-10,180 C120,150 210,220 330,150" stroke="#ffe08a" strokeWidth="13" fill="none" strokeLinecap="round" />
    <path d="M-10,180 C120,150 210,220 330,150" stroke="#f5c249" strokeWidth="13" fill="none" strokeLinecap="round" strokeDasharray="1 26" />
    {/* route */}
    <path d={ROUTE_D} fill="none" stroke="#ffffff" strokeWidth="11" strokeLinecap="round" />
    <path id="ct-route" d={ROUTE_D} fill="none" stroke="#1b8a25" strokeWidth="6" strokeLinecap="round" />
    {/* pickup dot */}
    <circle cx="64" cy="600" r="11" fill="#1b8a25" stroke="#fff" strokeWidth="4" />
    {/* dropoff pin */}
    <g transform="translate(250,64)">
      <path d="M0,-26 C14,-26 22,-16 22,-4 C22,10 0,26 0,26 C0,26 -22,10 -22,-4 C-22,-16 -14,-26 0,-26 Z" fill="#0a0a0a" />
      <circle cx="0" cy="-6" r="7" fill="#fff" />
    </g>
    {/* moving car for tracking */}
    {tracking && (
      <g>
        <circle r="13" fill="#1b8a25" stroke="#fff" strokeWidth="4" />
        <g transform="translate(-6,-6)" fill="#fff">
          <path d="M1.5,7 L2.6,3.3 C2.8,2.6 3.4,2.2 4.1,2.2 L7.9,2.2 C8.6,2.2 9.2,2.6 9.4,3.3 L10.5,7 L10.5,9.4 C10.5,9.7 10.3,9.9 10,9.9 L9.2,9.9 C8.9,9.9 8.7,9.7 8.7,9.4 L8.7,8.8 L3.3,8.8 L3.3,9.4 C3.3,9.7 3.1,9.9 2.8,9.9 L2,9.9 C1.7,9.9 1.5,9.7 1.5,9.4 Z" />
        </g>
        <animateMotion dur="7s" repeatCount="indefinite" rotate="auto" keyPoints="1;0" keyTimes="0;1" calcMode="linear">
          <mpath href="#ct-route" />
        </animateMotion>
      </g>
    )}
  </svg>
);

/* -------------------- Phone frame -------------------- */
const PhoneFrame = ({ children, testid }) => (
  <div className="relative mx-auto w-[290px] sm:w-[310px]" data-testid={testid}>
    <div className="absolute -inset-4 rounded-[56px] bg-brand/10 blur-2xl" aria-hidden="true" />
    <div className="relative rounded-[46px] bg-zinc-900 p-[10px] shadow-[0_40px_80px_-24px_rgba(0,0,0,0.55)] ring-1 ring-white/10">
      <div className="relative h-[600px] w-full overflow-hidden rounded-[38px] bg-white">
        <div className="absolute top-0 left-1/2 z-30 h-7 w-32 -translate-x-1/2 rounded-b-2xl bg-zinc-900" aria-hidden="true" />
        {children}
      </div>
    </div>
  </div>
);

const StatusBar = ({ light = false }) => (
  <div className={`flex items-center justify-between px-6 pt-3 pb-1 text-[12px] font-semibold ${light ? "text-white" : "text-zinc-900"}`}>
    <span>9:41</span>
    <span className="flex items-center gap-1">
      <span className="tracking-tight">5G</span>
      <span className="inline-block h-2.5 w-5 rounded-[3px] border border-current opacity-80" />
    </span>
  </div>
);

/* -------------------- Screen 1: Book a ride -------------------- */
const BookingScreen = () => (
  <div className="flex h-full flex-col">
    <div className="relative flex-1">
      <GMap />
      <div className="absolute inset-x-3 top-9 z-10 rounded-2xl bg-white/95 p-3 shadow-lg backdrop-blur">
        <div className="flex items-center gap-2.5 border-b border-zinc-100 pb-2.5">
          <span className="h-2.5 w-2.5 rounded-full bg-brand" />
          <span className="text-[13px] font-semibold text-zinc-800">Home · Crawley, RH10</span>
        </div>
        <div className="flex items-center gap-2.5 pt-2.5">
          <MapPin className="h-3.5 w-3.5 text-zinc-900" />
          <span className="text-[13px] font-semibold text-zinc-800">Gatwick Airport (LGW)</span>
        </div>
      </div>
    </div>
    <div className="rounded-t-3xl bg-white px-5 pt-4 pb-6 shadow-[0_-8px_24px_rgba(0,0,0,0.06)]">
      <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-zinc-200" />
      <p className="text-[11px] font-bold uppercase tracking-widest text-brand">Fixed fare</p>
      <div className="flex items-end justify-between">
        <h4 className="font-heading text-2xl font-bold text-zinc-900">£25.00</h4>
        <span className="flex items-center gap-1 text-[12px] font-medium text-zinc-500"><Clock className="h-3.5 w-3.5" /> 10 min away</span>
      </div>
      <button className="mt-4 w-full rounded-full bg-brand py-3 text-[15px] font-bold text-white">Confirm pickup</button>
    </div>
  </div>
);

/* -------------------- Screen 2: Live fares -------------------- */
const FARES = [
  { name: "Saloon", seats: "4 seats", price: "£25.00", sel: true },
  { name: "MPV Medium", seats: "5 seats", price: "£32.00" },
  { name: "Executive", seats: "3 seats", price: "£45.00" },
  { name: "Minibus", seats: "16 seats", price: "£70.00" },
];
const FareScreen = () => (
  <div className="flex h-full flex-col bg-white">
    <div className="relative h-44">
      <GMap />
    </div>
    <div className="flex-1 px-5 pt-4">
      <p className="text-[11px] font-bold uppercase tracking-widest text-brand">Choose your ride</p>
      <h4 className="font-heading text-xl font-bold text-zinc-900">Crawley → Gatwick</h4>
      <p className="mb-3 text-[12px] text-zinc-500">Fixed price · no surge · flight tracked</p>
      <div className="space-y-2">
        {FARES.map((f) => (
          <div key={f.name} className={`flex items-center justify-between rounded-2xl border px-3.5 py-2.5 ${f.sel ? "border-brand bg-brand/5" : "border-zinc-100 bg-white"}`}>
            <div className="flex items-center gap-3">
              <span className={`grid h-8 w-8 place-items-center rounded-full ${f.sel ? "bg-brand text-white" : "bg-zinc-100 text-zinc-500"}`}><Navigation className="h-4 w-4" /></span>
              <div>
                <p className="text-[13px] font-semibold text-zinc-800">{f.name}</p>
                <p className="text-[11px] text-zinc-400">{f.seats}</p>
              </div>
            </div>
            <span className="text-[14px] font-bold text-zinc-900">{f.price}</span>
          </div>
        ))}
      </div>
    </div>
    <div className="px-5 pb-6 pt-2">
      <button className="w-full rounded-full bg-brand py-3 text-[15px] font-bold text-white">Book Saloon · £25.00</button>
    </div>
  </div>
);

/* -------------------- Screen 3: Ride tracking -------------------- */
const TrackScreen = () => (
  <div className="flex h-full flex-col">
    <div className="relative flex-1">
      <GMap tracking />
      <div className="absolute inset-x-3 top-9 z-10 flex items-center justify-between rounded-2xl bg-zinc-900/90 px-4 py-2.5 text-white shadow-lg backdrop-blur">
        <span className="text-[13px] font-semibold">Arriving in 4 min</span>
        <span className="flex items-center gap-1 text-[12px] text-brand-light"><Navigation className="h-3.5 w-3.5" /> On the way</span>
      </div>
    </div>
    <div className="rounded-t-3xl bg-white px-5 pt-4 pb-6 shadow-[0_-8px_24px_rgba(0,0,0,0.06)]">
      <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-zinc-200" />
      <div className="flex items-center gap-3">
        <span className="grid h-12 w-12 place-items-center rounded-full bg-brand/10 font-heading text-lg font-bold text-brand">JD</span>
        <div className="flex-1">
          <p className="text-[14px] font-bold text-zinc-900">James D.</p>
          <p className="flex items-center gap-1 text-[12px] text-zinc-500"><Star className="h-3 w-3 fill-brand text-brand" /> 4.9 · Mercedes E-Class</p>
        </div>
        <span className="rounded-lg bg-zinc-900 px-2.5 py-1 text-[12px] font-bold tracking-wider text-white">CT26 CAR</span>
      </div>
      <div className="mt-4 flex gap-2">
        <button className="flex flex-1 items-center justify-center gap-2 rounded-full bg-brand py-2.5 text-[13px] font-bold text-white"><Phone className="h-4 w-4" /> Call</button>
        <button className="flex flex-1 items-center justify-center gap-2 rounded-full border border-zinc-200 py-2.5 text-[13px] font-bold text-zinc-800"><MessageSquare className="h-4 w-4" /> Message</button>
      </div>
    </div>
  </div>
);

/* -------------------- Store badges + payments -------------------- */
export const StoreButtons = ({ dark = false, testidPrefix = "store", className = "" }) => {
  const ring = dark ? "ring-1 ring-white/25" : "";
  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`}>
      <a
        href={APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`${testidPrefix}-app-store`}
        aria-label="Download Clocktower Cars on the App Store"
        className={`inline-block rounded-xl transition-transform duration-300 hover:scale-[1.04] ${ring}`}
      >
        <img src="/images/store/app-store.svg" alt="Download on the App Store" className="h-[52px] w-auto" />
      </a>
      <a
        href={GOOGLE_PLAY_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`${testidPrefix}-google-play`}
        aria-label="Get Clocktower Cars on Google Play"
        className={`inline-block rounded-xl transition-transform duration-300 hover:scale-[1.04] ${ring}`}
      >
        <img src="/images/store/google-play.png" alt="Get it on Google Play" className="h-[52px] w-auto" />
      </a>
    </div>
  );
};

const GoogleG = ({ className = "h-4 w-4" }) => (
  <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
    <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z" />
    <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z" />
    <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z" />
    <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z" />
  </svg>
);

const Chip = ({ children }) => (
  <span className="inline-flex h-10 items-center gap-1.5 rounded-xl border border-zinc-200 bg-white px-3 shadow-sm">{children}</span>
);

export const PaymentMethods = ({ dark = false, className = "" }) => (
  <div className={className}>
    <p className={`mb-3 text-xs font-semibold uppercase tracking-widest ${dark ? "text-white/50" : "text-ink/40"}`}>Pay your way</p>
    <div className="flex flex-wrap items-center gap-2.5">
      <Chip><Apple className="h-4 w-4 text-zinc-900" fill="currentColor" strokeWidth={0} /><span className="text-sm font-semibold text-zinc-900">Pay</span></Chip>
      <Chip><GoogleG /><span className="text-sm font-semibold text-zinc-700">Pay</span></Chip>
      <Chip><span className="font-heading text-base font-black italic tracking-tight text-[#1a1f71]">VISA</span></Chip>
      <Chip>
        <span className="relative inline-flex items-center">
          <span className="h-4 w-4 rounded-full bg-[#eb001b]" />
          <span className="-ml-2 h-4 w-4 rounded-full bg-[#f79e1b] mix-blend-multiply" />
        </span>
        <span className="text-[11px] font-semibold text-zinc-500">mastercard</span>
      </Chip>
    </div>
  </div>
);

/* -------------------- Band -------------------- */
const Band = ({ bg, textLight, eyebrow, title, desc, features, phone, reverse, showStore, testid }) => (
  <section className={`${bg} py-20 md:py-28`} data-testid={testid}>
    <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-2 lg:gap-24 lg:px-8">
      <motion.div {...fadeUp} className={reverse ? "lg:order-2" : ""}>
        <p className={`mb-4 text-xs font-bold uppercase tracking-[0.2em] ${textLight ? "text-brand-light" : "text-brand"}`}>{eyebrow}</p>
        <h2 className={`font-heading text-4xl font-bold leading-tight tracking-tight md:text-5xl ${textLight ? "text-white" : "text-ink"}`}>{title}</h2>
        <p className={`mt-5 max-w-md text-lg leading-relaxed ${textLight ? "text-white/70" : "text-ink/60"}`}>{desc}</p>
        <ul className="mt-7 space-y-3">
          {features.map((f) => (
            <li key={f} className={`flex items-center gap-3 text-[15px] ${textLight ? "text-white/90" : "text-ink/80"}`}>
              <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-full ${textLight ? "bg-white/15 text-brand-light" : "bg-brand/10 text-brand"}`}><Check className="h-3.5 w-3.5" strokeWidth={3} /></span>
              {f}
            </li>
          ))}
        </ul>
        {showStore && (
          <div className="mt-8 space-y-6">
            <StoreButtons dark={textLight} testidPrefix={testid} />
            <PaymentMethods dark={textLight} />
          </div>
        )}
      </motion.div>
      <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className={reverse ? "lg:order-1" : ""}>
        {phone}
      </motion.div>
    </div>
  </section>
);

export const AppShowcase = () => (
  <div data-testid="app-showcase">
    {/* Intro */}
    <section className="bg-white pt-24 pb-4 md:pt-28">
      <div className="mx-auto max-w-3xl px-6 text-center lg:px-8">
        <p className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-brand">The Clocktower Cars app</p>
        <h2 className="font-heading text-4xl font-bold tracking-tight text-ink md:text-5xl lg:text-6xl">Your driver, one tap away</h2>
        <p className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-ink/60">
          Book, price and track every journey from your phone. Live maps, fixed fares and real-time driver tracking — powered by Google Maps.
        </p>
        <div className="mt-9 flex flex-col items-center gap-6">
          <StoreButtons testidPrefix="app-intro" />
          <PaymentMethods className="flex flex-col items-center text-center" />
        </div>
      </div>
    </section>

    <Band
      testid="band-book"
      bg="bg-white"
      eyebrow="Book a ride"
      title="From your door to the terminal"
      desc="Set your pickup and destination on a live Google Map, see the road ahead and confirm in seconds. Airport waiting time and flight tracking are built in."
      features={["Live Google Maps routing", "Instant fixed-fare quote", "Save home & work addresses"]}
      phone={<PhoneFrame testid="phone-book"><StatusBar /><BookingScreen /></PhoneFrame>}
    />

    <Band
      testid="band-fares"
      bg="bg-ink"
      textLight
      reverse
      showStore
      eyebrow="Live fares"
      title="Know the price before you ride"
      desc="Compare every vehicle class with an upfront, all-inclusive fare. No meters, no surge pricing — just the honest price you see on screen."
      features={["Fixed fares, no surge", "Every vehicle class priced", "Pay by card, cash or account"]}
      phone={<PhoneFrame testid="phone-fares"><StatusBar /><FareScreen /></PhoneFrame>}
    />

    <Band
      testid="band-track"
      bg="bg-brand"
      textLight
      eyebrow="Track your ride"
      title="Watch your car arrive in real time"
      desc="Follow your driver on the map as they approach, see a live ETA and message or call them in a tap. You always know exactly where your ride is."
      features={["Real-time driver tracking", "Live ETA & arrival alerts", "Call or message your driver"]}
      phone={<PhoneFrame testid="phone-track"><StatusBar light /><TrackScreen /></PhoneFrame>}
    />
  </div>
);
